import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getCompletionStepIds,
  FLAT_STEP_CONFIGS,
} from '@/features/guided-financial-planning/config';
import {
  guidedPlanningApi,
  guidedPlanningSessionKeys,
} from '@/features/guided-financial-planning/api';

const DEFAULT_COMPLETION_STEP_IDS = getCompletionStepIds(FLAT_STEP_CONFIGS);

function selectActiveSession(
  sessions: GuidedPlanningSessionResponse[] | undefined,
): GuidedPlanningSessionResponse | undefined {
  if (!sessions || sessions.length === 0) return undefined;

  const inProgressSession = sessions.find(s => s.status === 'in_progress');
  if (inProgressSession) return inProgressSession;

  const completedSession = sessions.find(s => s.status === 'completed');
  if (completedSession) return completedSession;

  return sessions[0];
}

export function useGuidedPlanningSession(
  clientId: string,
  completionStepIds: GuidedPlanningStep[] = DEFAULT_COMPLETION_STEP_IDS,
  { enabled = true }: { enabled?: boolean } = {},
) {
  const queryClient = useQueryClient();

  const {
    data: fetchedSessions,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: guidedPlanningSessionKeys.list({ clientId }),
    queryFn: () => guidedPlanningApi.getSessions(clientId),
    enabled: Boolean(clientId) && enabled,
    staleTime: 2 * 60 * 1000,
  });
  // A caller that opts out never sees sessions, even ones another screen has cached.
  const sessions = enabled ? fetchedSessions : undefined;

  const session = useMemo(() => selectActiveSession(sessions), [sessions]);

  // Mutations read the cache rather than the render they were created in, so a save that follows
  // another in the same handler (reopening a completed session, then saving an edit) sees its result.
  const getLatestSession = () =>
    enabled
      ? (selectActiveSession(
          queryClient.getQueryData<GuidedPlanningSessionResponse[]>(
            guidedPlanningSessionKeys.list({ clientId }),
          ),
        ) ?? session)
      : session;

  const upsertMutation = useMutation({
    mutationFn: (data: PutGuidedPlanningSessionBody) =>
      guidedPlanningApi.upsertSession(clientId, data),
    onSuccess: updatedSession => {
      queryClient.setQueryData(
        guidedPlanningSessionKeys.list({ clientId }),
        (oldSessions: GuidedPlanningSessionResponse[] | undefined) => {
          if (!oldSessions) return [updatedSession];
          const index = oldSessions.findIndex(s => s.id === updatedSession.id);
          if (index >= 0) {
            const newSessions = [...oldSessions];
            newSessions[index] = updatedSession;
            return newSessions;
          }
          return [updatedSession, ...oldSessions];
        },
      );
    },
  });

  const updateStep = (step: GuidedPlanningStep, markPreviousComplete = true) => {
    const current = getLatestSession();
    if (!current?.id || current.status === 'completed') {
      return Promise.resolve(undefined);
    }

    const completedSteps = markPreviousComplete
      ? [...new Set([...(current.completedSteps ?? []), current.currentStep])]
      : (current.completedSteps ?? []);

    return upsertMutation.mutateAsync({
      sessionId: current.id,
      currentStep: step,
      completedSteps: completedSteps as GuidedPlanningStep[],
    });
  };

  const updateStepData = (newData: GuidedPlanningStepData) => {
    const current = getLatestSession();
    if (!current?.id || current.status === 'completed') {
      return Promise.resolve(undefined);
    }

    return upsertMutation.mutateAsync({
      sessionId: current.id,
      stepData: { ...current.stepData, ...newData },
    });
  };

  const completeStepWithData = (
    nextStep: GuidedPlanningStep,
    stepData?: GuidedPlanningStepData,
  ) => {
    const current = getLatestSession();
    if (!current?.id || current.status === 'completed') {
      return Promise.resolve(undefined);
    }

    const completedSteps = [
      ...new Set([...(current.completedSteps ?? []), current.currentStep]),
    ] as GuidedPlanningStep[];

    return upsertMutation.mutateAsync({
      sessionId: current.id,
      currentStep: nextStep,
      completedSteps,
      stepData: stepData ? { ...current.stepData, ...stepData } : undefined,
    });
  };

  const completeSession = () => {
    const current = getLatestSession();
    if (!current?.id || current.status === 'completed') {
      return Promise.resolve(undefined);
    }

    return upsertMutation.mutateAsync({
      sessionId: current.id,
      status: 'completed',
      completedSteps: completionStepIds,
    });
  };

  /**
   * Puts a completed session back in progress. `stepData` replaces the session's step data
   * whole, so the caller can drop the finalization snapshots in the same write.
   */
  const reopenSession = (stepData: GuidedPlanningStepData) => {
    const current = getLatestSession();
    if (!current?.id || current.status !== 'completed') {
      return Promise.resolve(undefined);
    }

    return upsertMutation.mutateAsync({
      sessionId: current.id,
      status: 'in_progress',
      stepData,
    });
  };

  const abandonSession = () => {
    const current = getLatestSession();
    if (!current?.id) return Promise.resolve(undefined);

    return upsertMutation.mutateAsync({
      sessionId: current.id,
      status: 'abandoned',
    });
  };

  return {
    session,
    sessions,
    isLoading,
    isError,
    error,
    isSaving: upsertMutation.isPending,
    updateStep,
    updateStepData,
    completeStepWithData,
    completeSession,
    reopenSession,
    abandonSession,
    refetch,
  };
}
